import React, {type JSX} from 'react';
import {styled} from '@mui/material/styles';
import {ViewerScreenError} from '../viewerScreenError';
import {useDicomViewer} from './useDicomViewer';
import {type DicomError} from './dicomErrors';
import {DicomLoadingScreen} from './dicomLoadingScreen';

const ViewerWrapper = styled('div')({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'auto',
  backgroundColor: 'black',
});

const DicomElement = styled('div')({
  width: '600px',
  height: '600px',
  position: 'absolute',
  right: 0,
  left: 0,
  top: 0,
  bottom: 0,
  margin: 'auto',
});

const ErrorOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function getErrorMessage(error: DicomError): string {
  switch (error.type) {
    case 'network': {
      return 'Unable to load the medical image due to a network issue. Please check your connection and try again.';
    }

    case 'file-format': {
      return 'The selected file does not appear to be a valid DICOM image or may be corrupted.';
    }

    case 'rendering': {
      return 'Unable to display the medical image. Please try refreshing the page.';
    }

    case 'initialization': {
      return 'Failed to initialize the medical image viewer. Please refresh the page and try again.';
    }

    default: {
      return 'An unexpected error occurred while loading the medical image viewer.';
    }
  }
}

type Props = {
  src: string;
};

export function ViewScreenDICOM({src}: Readonly<Props>): JSX.Element {
  const elementId = 'cornerstone-element';
  const {isLoading, error, retryLoad} = useDicomViewer(src, elementId);

  return (
    <ViewerWrapper>
      <DicomElement id={elementId} />

      {isLoading && <DicomLoadingScreen />}

      {error && (
        <ErrorOverlay>
          <ViewerScreenError
            title="Medical Image Viewer Error"
            description={getErrorMessage(error)}
            onRetry={retryLoad}
          />
        </ErrorOverlay>
      )}
    </ViewerWrapper>
  );
}
